const FIREBASE_STAGING_SITE_IDS = [
  'frontend-test-environment',
  'cgo-hazteoir-staging',
];
const FIREBASE_HOST_SUFFIXES = ['.web.app', '.firebaseapp.com'];
const FIREBASE_DEFAULT_STAGING_HOSTS = new Set(
  FIREBASE_STAGING_SITE_IDS.flatMap((siteId) => (
    FIREBASE_HOST_SUFFIXES.map((suffix) => `${siteId}${suffix}`)
  )),
);
const STAGING_CITIZENGO_HOST = 'staging.citizengo.org';
const STAGING_HAZTEOIR_HOST = 'staging.hazteoir.org';
const BACKEND_VALIDATION_SITE_ID = 'cgo-backend-prod-validation';
const BACKEND_VALIDATION_CUSTOM_HOST = 'panels.citizengo.org';
const HAZTEOIR_PATH_PREFIX = '/hazteoir';

export const FIREBASE_STAGING_API_URL = 'https://cgo-backend-acceptance-437481211286.europe-west1.run.app';
export const BACKEND_VALIDATION_API_URL = process.env.REACT_APP_BACKEND_VALIDATION_API_URL
  || FIREBASE_STAGING_API_URL;
export const HAZTEOIR_LANGUAGE = 'hazteoir';
export const DONATION_PRODUCTION_URL = 'https://donate.citizengo.org';
export const DONATION_HAZTEOIR_PRODUCTION_URL = 'https://donate.hazteoir.org';
export const DONATION_SANDBOX_URL = 'https://sandbox.donate.citizengo.org';

export const getCurrentHost = () => window.location.hostname.toLowerCase();

const isFirebaseSiteHost = (candidateHost, siteId) => {
  const normalizedHost = candidateHost.toLowerCase();

  return FIREBASE_HOST_SUFFIXES.some((suffix) => (
    normalizedHost === `${siteId}${suffix}`
    || (
      normalizedHost.startsWith(`${siteId}--`)
      && normalizedHost.endsWith(suffix)
    )
  ));
};

export const isHazteoirHost = (candidateHost = getCurrentHost()) => {
  const normalizedHost = candidateHost.toLowerCase();
  return normalizedHost === 'hazteoir.org'
    || normalizedHost.endsWith('.hazteoir.org')
    || isFirebaseSiteHost(normalizedHost, 'cgo-hazteoir-staging');
};

export const isStagingCustomDomain = (candidateHost = getCurrentHost()) => {
  const normalizedHost = candidateHost.toLowerCase();
  return normalizedHost === STAGING_CITIZENGO_HOST || normalizedHost === STAGING_HAZTEOIR_HOST;
};

export const isFirebaseDefaultStagingHost = (candidateHost = getCurrentHost()) => {
  const normalizedHost = candidateHost.toLowerCase();
  return FIREBASE_DEFAULT_STAGING_HOSTS.has(normalizedHost);
};

export const isFirebaseStagingHost = (candidateHost = getCurrentHost()) => {
  return FIREBASE_STAGING_SITE_IDS.some((siteId) => isFirebaseSiteHost(candidateHost, siteId))
    || isStagingCustomDomain(candidateHost);
};

export const isBackendValidationHost = (candidateHost = getCurrentHost()) => {
  const normalizedHost = candidateHost.toLowerCase();

  return normalizedHost === BACKEND_VALIDATION_CUSTOM_HOST
    || isFirebaseSiteHost(normalizedHost, BACKEND_VALIDATION_SITE_ID);
};

export const getBackendValidationAdminUrl = ({
  candidateHost = getCurrentHost(),
  direction,
  region,
  accounts,
  nid,
}) => {
  if (!isBackendValidationHost(candidateHost)) {
    return null;
  }

  const query = new URLSearchParams({
    region: region === HAZTEOIR_LANGUAGE ? 'es-es' : region,
  });

  if (nid) {
    query.set('pid', nid);
  }

  const backendOrigin = BACKEND_VALIDATION_API_URL.replace(/\/+$/, '');
  return `${backendOrigin}/admin/${accounts}/${direction}?${query.toString()}`;
};

export const getDonationBaseUrl = (candidateHost = getCurrentHost()) => {
  if (isFirebaseStagingHost(candidateHost) || isBackendValidationHost(candidateHost)) {
    return DONATION_SANDBOX_URL;
  }

  if (isHazteoirHost(candidateHost)) {
    return DONATION_HAZTEOIR_PRODUCTION_URL;
  }

  return DONATION_PRODUCTION_URL;
};

export const shouldUseCurrentFrontendOrigin = (candidateHost = getCurrentHost()) => {
  return isStagingCustomDomain(candidateHost)
    || isHazteoirHost(candidateHost)
    || isBackendValidationHost(candidateHost);
};

export const getForcedLocaleForHost = (candidateHost = getCurrentHost()) => {
  if (isHazteoirHost(candidateHost)) {
    return HAZTEOIR_LANGUAGE;
  }

  return undefined;
};

export const getCanonicalPathForHost = (
  pathname = window.location.pathname,
  candidateHost = getCurrentHost(),
) => {
  if (!isHazteoirHost(candidateHost)) {
    return pathname;
  }

  if (pathname === HAZTEOIR_PATH_PREFIX) {
    return '/';
  }

  if (pathname.startsWith(`${HAZTEOIR_PATH_PREFIX}/`)) {
    return pathname.replace(HAZTEOIR_PATH_PREFIX, '') || '/';
  }

  return pathname;
};

export const getDefaultLocalePath = (
  candidateHost = getCurrentHost(),
  detectedLanguage = '',
) => {
  if (isHazteoirHost(candidateHost)) {
    return '/';
  }

  return detectedLanguage || 'en-row';
};
